<template>
  <div class="enrollment-container">
    <div class="header">
      <h3>Student Enrollment</h3>
      <button @click="showEnrollmentModal = true">+ Add Student</button>
    </div>

    <div v-if="enrollments.length > 0" class="enrollment-table">
      <table>
        <thead>
          <tr>
            <th>Student ID</th>
            <th>Name</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="enrollment in enrollments" :key="enrollment.student_id">
            <td>{{ enrollment.student.student_id }}</td>
            <td>{{ enrollment.student.name }}</td>
            <td>{{ enrollment.status || 'Active' }}</td>
            <td>
              <button @click="openEditModal(enrollment)">Edit</button>
              <button @click="confirmDelete(enrollment.student_id)">Delete</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-else class="no-enrollments">
      No students enrolled yet
    </div>

    <StudentCourseModal
      v-if="showEnrollmentModal || currentEnrollment"
      :courseId="course.course_id"
      :enrollment="currentEnrollment"
      :availableStudents="availableStudents"
      @save="handleSave"
      @close="closeModal"
    />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useQuery, useMutation } from '@vue/apollo-composable';
import StudentCourseModal from './StudentCourseModal.vue';
import { GET_STUDENTS_IN_COURSE, GET_STUDENTS, DELETE_STUDENT_COURSE } from '../graphql/queries';

const props = defineProps({
  course: {
    type: Object,
    required: true
  }
})

const showEnrollmentModal = ref(false)
const currentEnrollment = ref(null)

// Fetch enrollments
const { result: enrollmentResult, refetch: refetchEnrollments } = useQuery(
  GET_STUDENTS_IN_COURSE,
  () => ({ course_id: props.course.course_id }),
  { 
    enabled: computed(() => !!props.course.course_id), // Only run the query if course_id is available
    fetchPolicy: 'network-only',
  }
);

// Fetch students not yet enrolled
const { result: studentsResult } = useQuery(GET_STUDENTS);

const enrollments = computed(() => enrollmentResult.value?.studentsInCourse || [])
const availableStudents = computed(() => studentsResult.value?.students || [])

// Delete enrollment
const { mutate: deleteEnrollment } = useMutation(DELETE_STUDENT_COURSE);
const confirmDelete = (studentId) => {
  if (confirm('Are you sure you want to remove this student from the course?')) {
    deleteEnrollment({
      course_id: props.course.course_id,
      student_id: studentId
    }).then(() => refetchEnrollments());
  }
};

// Modal handlers
const openEditModal = (enrollment) => {
  currentEnrollment.value = enrollment
}

const closeModal = () => {
  showEnrollmentModal.value = false
  currentEnrollment.value = null
}

const handleSave = () => {
  closeModal()
  refetchEnrollments()
}
</script>

<style scoped>
.enrollment-container {
  margin-top: 2rem;
  border-top: 1px solid #eee;
  padding-top: 1rem;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
}

th, td {
  padding: 0.75rem;
  border: 1px solid #ddd;
  text-align: left;
}

th {
  background-color: #f5f5f5;
}

button {
  margin-right: 0.5rem;
  padding: 0.375rem 0.75rem;
  background-color: #f0f0f0;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
}

button:hover {
  background-color: #e0e0e0;
}

.no-enrollments {
  padding: 1rem;
  background-color: #f9f9f9;
  border: 1px dashed #ddd;
  text-align: center;
  color: #666;
}
</style>